// @ts-strict-ignore
import {AppRoutingModule} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {ErrorHandler, inject, NgModule, provideAppInitializer} from "@angular/core";
import {BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig} from "@angular/platform-browser";
import {TranslateLoader, TranslateModule, TranslateService} from "@ngx-translate/core";
import {SharedModule} from "./shared/shared.module";
import {
	SimClientStorageService,
	SimConfigurationService,
	SimDeviceMappingService,
	SimHttpInterceptor,
	SimInjectableDataService,
	SimLocalizedResourceService,
	SimSystemExtendedService,
	SimUserExtendedService,
	SimUtilityService,
	SimWebpagePermissionService,
	SystemHub,
} from "@services";
import {firstValueFrom, from, Observable} from "rxjs";
import {DialogModule} from "./modules/dialog/dialog.module";
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions} from "@angular/material/tooltip";
import Hammer from "hammerjs";
import {SimErrorHandler} from "./shared/errorhandler/sim-error-handler";
import {HTTP_INTERCEPTORS} from "@angular/common/http";
import {EventEmitterService} from "@event-emitter";
import dayjs from "dayjs";
import {DeviceService} from "./core/services/sim-services/device.service";
import {APP_BASE_HREF, DATE_PIPE_DEFAULT_OPTIONS} from "@angular/common";
import {BaseHubService} from "./api/hub-service-base.service";
import {provideCharts, withDefaultRegisterables} from "ng2-charts";
import {LogoComponent} from "./shared/shared-components/logo/logo.component";
import {SimButton} from "@common/ui/button/sim-button.component";
import {EnvironmentService} from "./shared/shared-components/environment-overlay/environment.service";
import {SideNavigationMenuComponent} from "@common/components/side-navigation-menu/side-navigation-menu.component";

export const CUSTOM_SIM_TOOLTIP_OPTIONS: MatTooltipDefaultOptions = {
	showDelay: 0,
	hideDelay: 0,
	touchendHideDelay: 0,
	disableTooltipInteractivity: true,
};

export class SIMSTATION_HAMMER_JS_CONF extends HammerGestureConfig {
	buildHammer(element: HTMLElement) {
		const mc = new Hammer(element, {
			touchAction: "pan-y",
		});

		return mc;
	}
}

class SimTranslateLoader implements TranslateLoader {
	public constructor(protected localizedResourceService: SimLocalizedResourceService) {}

	getTranslation(lang: string): Observable<any> {
		return from(this.localizedResourceService.getAllLocalizedStrings(lang));
	}
}

const initializeAppProvider = provideAppInitializer(() =>
	initializeApp(
		inject(SimUserExtendedService),
		inject(SimConfigurationService),
		inject(SimDeviceMappingService),
		inject(SimWebpagePermissionService),
		inject(SimClientStorageService),
		inject(EventEmitterService),
		inject(DeviceService),
		inject(TranslateService),
		inject(SimInjectableDataService),
		// Currently not working comment in to enable mocking service
		// inject(RequestMockingService),
		inject(SystemHub),
		inject(EnvironmentService),
	),
);

/**
 * Initializes app prior of App rendering
 */
async function initializeApp(
	userService: SimUserExtendedService,
	appConfig: SimConfigurationService,
	deviceMappingService: SimDeviceMappingService,
	webpagePermissionService: SimWebpagePermissionService,
	simClientStorageService: SimClientStorageService,
	eventEmitter: EventEmitterService,
	deviceService: DeviceService,
	translate: TranslateService,
	injectableDeviceDataService: SimInjectableDataService,
	systemHub: SystemHub,
	environmentService: EnvironmentService,
): Promise<unknown> {
	await appConfig.reloadConfig();

	// If the application runs in electron, wait for the device id to be set
	if (window.electron && !injectableDeviceDataService.clientData?.deviceId) {
		console.info("Electron detected - waiting for device id to be set");
		let tries = 10;
		while (tries-- > 0 && !injectableDeviceDataService.clientData?.deviceId) {
			console.info("waiting for device id to be set...");
			await SimUtilityService.delay(200);
		}
	}

	environmentService.handleEnvironmentDeviceIdChanges();

	// the device id should be set before the websocket connection is configured
	await deviceService.registerDevice();
	await ensureHubIsConnected(systemHub);

	let userPromise = userService.init();
	let devicePromise = deviceMappingService.reloadClientProperties();
	let webPagePromise = webpagePermissionService.load();

	let getLastLang = () => {
		let visibleLangs = SimConfigurationService.settings.visibleLanguages ?? [];
		let lastSelectedLang = simClientStorageService.getLocalData("selectedLanguage");
		if (!visibleLangs?.includes(lastSelectedLang)) {
			lastSelectedLang = SimConfigurationService.settings.visibleLanguages?.[0];
		}
		return lastSelectedLang;
	};

	return Promise.all([userPromise, devicePromise, webPagePromise]).then(() => {
		// this language will be used as a fallback when a translation isn't found in the current language
		translate.setDefaultLang("en");
		// the lang to use, if the lang isn't available, it will use the current loader to get them
		translate.use(getLastLang());

		eventEmitter.onToggleShowTranslationKeys.subscribe((args) => {
			if (args.visible) {
				// this language will be used as a fallback when a translation isn't found in the current language
				translate.setDefaultLang("SHOWKEYS");

				// the lang to use, if the lang isn't available, it will use the current loader to get them
				translate.use("SHOWKEYS");
			} else {
				// this language will be used as a fallback when a translation isn't found in the current language
				translate.setDefaultLang("en");

				// the lang to use, if the lang isn't available, it will use the current loader to get them
				translate.use(getLastLang());
			}
		});

		translate.onLangChange.subscribe((evt) => {
			let lang = evt.lang;
			if (lang != "SHOWKEYS") {
				simClientStorageService.setLocalData("selectedLanguage", evt.lang);
				dayjs.locale(evt.lang);
			}
		});

		return firstValueFrom(translate.get("GLOBAL_TEXT_SIMSTATION"));
	});
}

async function ensureHubIsConnected(hub: BaseHubService): Promise<boolean> {
	if (hub.isConnected()) {
		return true;
	} else {
		const result = await hub.connect();
		if (!result.success) {
			console.error("Could not connect system hub");
		}
		return result.success;
	}
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useClass: SimTranslateLoader,
				deps: [SimLocalizedResourceService],
			},
		}),
		SharedModule,
		DialogModule,
		LogoComponent,
		SimButton,
		SideNavigationMenuComponent,
	],
	providers: [
		SimConfigurationService,
		SimDeviceMappingService,
		SimUserExtendedService,
		EventEmitterService,
		initializeAppProvider,
		SimSystemExtendedService,
		{
			provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
			useValue: CUSTOM_SIM_TOOLTIP_OPTIONS,
		},
		{
			provide: HAMMER_GESTURE_CONFIG,
			useClass: SIMSTATION_HAMMER_JS_CONF,
		},
		{provide: ErrorHandler, useClass: SimErrorHandler},
		{provide: HTTP_INTERCEPTORS, useClass: SimHttpInterceptor, multi: true},
		{provide: APP_BASE_HREF, useValue: "/"},
		{
			provide: DATE_PIPE_DEFAULT_OPTIONS,
			useFactory: () => {
				const options = {timezone: SimUtilityService.getTimezoneOffsetString()};
				console.log("DATE_PIPE_DEFAULT_OPTIONS", options);
				return options;
			},
		},
		provideCharts(withDefaultRegisterables()),
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
